import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';

/**
 * ŞAHLANMA Komuta Merkezi — Insight Engine (algı) + Smart Campaign Engine
 * (aksiyon) tek ekranda. Üstte içgörü feed'i (Önizle/Uygula), altta uygulanan
 * aksiyonların sonuç/geçmiş tablosu (geri al + onayla).
 *
 * Co-pilot: hiçbir şey otomatik canlıya gitmez. DRY-RUN açıkken "Uygula" yalnız
 * simüle eder ve aksiyon kaydı düşer. Backend: /api/insights (köprü uçları).
 */

const API = 'https://api.glowytics.com/api/insights';
const brandHeader = () => ({ 'X-Brand-ID': localStorage.getItem('currentBrandId') || '' });

const SEV = {
  critical: { ring: 'border-rose-200', bg: 'bg-rose-50', dot: 'bg-rose-500', text: 'text-rose-700', label: 'Kritik' },
  warning: { ring: 'border-amber-200', bg: 'bg-amber-50', dot: 'bg-amber-500', text: 'text-amber-700', label: 'Uyarı' },
  positive: { ring: 'border-emerald-200', bg: 'bg-emerald-50', dot: 'bg-emerald-500', text: 'text-emerald-700', label: 'Fırsat' },
  info: { ring: 'border-sky-200', bg: 'bg-sky-50', dot: 'bg-sky-500', text: 'text-sky-700', label: 'Bilgi' },
};
const SOURCE_BADGE = {
  meta: 'bg-indigo-100 text-indigo-700',
  'google ads': 'bg-blue-100 text-blue-700',
  'rtb house': 'bg-fuchsia-100 text-fuchsia-700',
  cross_channel: 'bg-violet-100 text-violet-700',
  tiktok: 'bg-gray-800 text-white',
  pinterest: 'bg-red-100 text-red-700',
  criteo: 'bg-orange-100 text-orange-700',
};
const fmtTRY = (n) => Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 });
const fmtTime = (t) => (t ? new Date(t).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

const ACTION_LABEL = {
  budget_increase: 'Bütçe artır',
  budget_decrease: 'Bütçe azalt',
  campaign_pause: 'Kampanya duraklat',
  rollback: 'Geri alma',
};

// ── Plan preview box (shown after Önizle) ────────────────────────────────────
function PlanBox({ plan }) {
  if (!plan) return null;
  if (!plan.applicable) {
    const reasons = {
      no_action_mapping: 'Bu içgörü tipi için tanımlı otomatik aksiyon yok.',
      unsupported_platform: 'Bu kanal için köprü henüz desteklenmiyor (yalnız Meta + Google Ads).',
      no_campaign_evidence: 'Kampanya kanıtı yok — hedef kampanya çözülemedi.',
    };
    return (
      <div className="mt-2 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-[12px] text-gray-500">
        ⓘ {reasons[plan.reason] || 'Uygulanabilir aksiyon yok.'}
      </div>
    );
  }
  const up = plan.changePct > 0;
  return (
    <div className="mt-2 rounded-lg bg-white border border-gray-200 px-3 py-2.5 text-[12px]">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="font-semibold text-gray-700">{ACTION_LABEL[plan.op] || plan.op}</span>
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${up ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>{plan.changeLabel}</span>
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{plan.platform}</span>
      </div>
      <p className="text-gray-600 truncate" title={plan.campaign_name}>🎯 {plan.campaign_name}</p>
      {plan.op !== 'campaign_pause' && (
        <p className="text-gray-500 mt-0.5 tabular-nums">
          Günlük bütçe: <b className="text-gray-700">{fmtTRY(plan.currentDaily)}₺</b> → <b className="text-gray-700">{fmtTRY(plan.proposedDaily)}₺</b>
        </p>
      )}
      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {plan.dryRun && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">DRY-RUN (simülasyon)</span>}
        {plan.needsEnroll && <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-700">Kampanya yönetime alınacak</span>}
        {plan.requiresApproval && <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">Onay gerekir (&gt;%{plan.approvalThreshold})</span>}
      </div>
    </div>
  );
}

// ── Hygiene evidence: affected campaigns (compact) ───────────────────────────
function HygieneEvidence({ ev }) {
  const camps = (ev && ev.campaigns) || [];
  if (!camps.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {camps.slice(0, 8).map((c, i) => (
        <span key={i} className="text-[10px] bg-white/70 border border-gray-200 text-gray-600 rounded px-1.5 py-0.5" title={`${fmtTRY(c.recent_spend)}₺ · ROAS ${c.recent_roas}x`}>
          {c.name}
          {c.detail_pct != null && <b className="ml-1 text-gray-500">{c.detail_pct}%</b>}
          {c.bid_strategy && <b className="ml-1 text-gray-500">{c.bid_strategy}</b>}
        </span>
      ))}
      {camps.length > 8 && <span className="text-[10px] text-gray-400">+{camps.length - 8}</span>}
    </div>
  );
}

// ── Insight card with Önizle / Uygula ────────────────────────────────────────
function Card({ ins, onApplied, onDismiss }) {
  const s = SEV[ins.severity] || SEV.info;
  const isHygiene = ins.kind === 'hygiene';
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  const preview = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/${ins.id}/preview`, { method: 'POST', credentials: 'include', headers: brandHeader() });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      setPlan(j.plan);
    } catch (e) { toast.error('Önizleme alınamadı. ' + (e.message || '')); }
    finally { setBusy(false); }
  };

  const apply = async (confirmEnroll = false) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/${ins.id}/apply`, {
        method: 'POST', credentials: 'include',
        headers: { ...brandHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmEnroll }),
      });
      const j = await res.json();
      if (res.status === 409 && j.needsEnroll) {
        setPlan(j.plan);
        if (window.confirm(`"${j.plan.campaign_name}" kampanyası henüz Glowytics yönetiminde değil.\n\nKöprüyle yönetime alınıp (duraklatılmış) aksiyon uygulansın mı?${j.plan.dryRun ? '\n\n(DRY-RUN açık — gerçek kampanyaya dokunulmaz, yalnız simüle edilir.)' : ''}`)) {
          return apply(true);
        }
        return;
      }
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      toast.success(j.dryRun
        ? `Simüle edildi (DRY-RUN)${j.requiresApproval ? ' · onay bekliyor' : ''}`
        : (j.requiresApproval ? 'Onaya gönderildi' : (j.executed ? 'Uygulandı' : 'Kaydedildi')));
      onApplied(ins.id);
    } catch (e) { toast.error('Uygulanamadı. ' + (e.message || '')); }
    finally { setBusy(false); }
  };

  return (
    <div className={`rounded-xl border ${s.ring} ${s.bg} p-4 flex gap-3`}>
      <div className={`mt-1 h-2.5 w-2.5 rounded-full flex-shrink-0 ${s.dot}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className={`text-[10px] font-semibold uppercase tracking-wide ${s.text}`}>{s.label}</span>
          {isHygiene && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-teal-100 text-teal-700">🔧 HİJYEN</span>
          )}
          {ins.source && (
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${SOURCE_BADGE[ins.source] || 'bg-gray-100 text-gray-600'}`}>
              {String(ins.source).toUpperCase()}
            </span>
          )}
        </div>
        <p className="text-sm font-semibold text-gray-800">{ins.title}</p>
        {ins.ai_narrative ? (
          <p className="text-[12px] text-gray-700 mt-1 leading-relaxed">
            <span className="text-[10px] font-semibold text-violet-600 mr-1">✦ Glowy</span>{ins.ai_narrative}
          </p>
        ) : (
          <p className="text-[12px] text-gray-600 mt-1 leading-relaxed">{ins.detail}</p>
        )}
        {(ins.ai_impact || ins.impact_hint) && (
          <p className="text-[11px] font-medium text-gray-700 mt-1.5 bg-white/70 rounded px-2 py-1 inline-block border border-gray-100">
            📈 {ins.ai_impact || ins.impact_hint}
          </p>
        )}

        {isHygiene && <HygieneEvidence ev={ins.evidence} />}

        {isHygiene && ins.recommended_action && (
          <div className="mt-2 flex items-start gap-1.5 text-[12px] text-gray-700 bg-white/70 rounded-lg px-3 py-2 border border-gray-100">
            <span className="text-gray-400">⇒</span><span>{ins.recommended_action}</span>
          </div>
        )}

        {!isHygiene && <PlanBox plan={plan} />}

        <div className="mt-2.5 flex items-center gap-2">
          {!isHygiene && (
            <>
              <button onClick={preview} disabled={busy}
                className="text-[12px] px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                {busy ? '…' : '👁 Önizle'}
              </button>
              <button onClick={() => apply(false)} disabled={busy}
                className="text-[12px] px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 font-medium">
                ⚡ Uygula
              </button>
            </>
          )}
          {isHygiene && <span className="text-[11px] text-gray-400">Aksiyon yakında (önce tespit)</span>}
          <button onClick={() => onDismiss(ins.id)} disabled={busy}
            className="text-[12px] px-2 py-1.5 rounded-lg text-gray-400 hover:text-gray-600 ml-auto">Kapat</button>
        </div>
      </div>
    </div>
  );
}

// ── Actions / results table ──────────────────────────────────────────────────
function ActionsTable({ actions, onRollback, onApprove }) {
  if (!actions.length) {
    return <div className="rounded-xl border border-dashed p-8 text-center text-gray-400 text-sm">Henüz uygulanan aksiyon yok.</div>;
  }
  return (
    <div className="rounded-xl border bg-white overflow-hidden">
      <table className="w-full text-[12px]">
        <thead className="bg-gray-50 text-gray-500">
          <tr>
            <th className="text-left font-medium px-3 py-2">Zaman</th>
            <th className="text-left font-medium px-3 py-2">Aksiyon</th>
            <th className="text-left font-medium px-3 py-2">Kampanya</th>
            <th className="text-right font-medium px-3 py-2">Değişim</th>
            <th className="text-left font-medium px-3 py-2">Durum</th>
            <th className="text-right font-medium px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {actions.map((a) => {
            const dry = a.metrics_snapshot && a.metrics_snapshot.dry_run;
            const pending = a.approval_status === 'pending';
            return (
              <tr key={a.id} className={`border-t border-gray-100 ${a.is_rolled_back ? 'opacity-50' : ''}`}>
                <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{fmtTime(a.executed_at)}</td>
                <td className="px-3 py-2">
                  <span className="font-medium text-gray-700">{ACTION_LABEL[a.action_type] || a.action_type}</span>
                  <span className="ml-1 text-[10px] text-gray-400">{a.platform}</span>
                </td>
                <td className="px-3 py-2 text-gray-600 truncate max-w-[200px]" title={a.campaign_name}>{a.campaign_name}</td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-600 whitespace-nowrap">
                  {a.field_name === 'status' ? `${a.old_value}→${a.new_value}` : `${fmtTRY(a.old_value)}→${fmtTRY(a.new_value)}₺`}
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {a.is_rolled_back ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">Geri alındı</span>
                      : a.action_type === 'rollback' ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">Düzeltme</span>
                      : !a.success ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700" title={a.error_message}>Hata</span>
                      : pending ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">Onay bekliyor</span>
                      : <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">✓ Tamam</span>}
                    {dry && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">DRY</span>}
                  </div>
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {pending && !a.is_rolled_back && (
                    <>
                      <button onClick={() => onApprove(a.id, true)} className="text-[11px] px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 mr-1">Onayla</button>
                      <button onClick={() => onApprove(a.id, false)} className="text-[11px] px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">Reddet</button>
                    </>
                  )}
                  {!pending && !a.is_rolled_back && a.action_type !== 'rollback' && (
                    <button onClick={() => onRollback(a.id)} className="text-[11px] px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">↩ Geri al</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function SahlanmaCommandCenter() {
  const [data, setData] = useState(null);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadInsights = useCallback(async () => {
    const res = await fetch(`${API}/overview`, { credentials: 'include', headers: brandHeader() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }, []);
  const loadActions = useCallback(async () => {
    const res = await fetch(`${API}/actions`, { credentials: 'include', headers: brandHeader() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const j = await res.json();
    return j.actions || [];
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [ins, acts] = await Promise.all([loadInsights(), loadActions()]);
      setData(ins); setActions(acts);
    } catch (e) {
      setError(e.message || 'Veri alınamadı');
      toast.error('Veri alınamadı. ' + (e.message || ''));
    } finally { setLoading(false); }
  }, [loadInsights, loadActions]);

  useEffect(() => { refresh(); }, [refresh]);

  const onApplied = async (id) => {
    setData((d) => d && {
      ...d,
      risks: d.risks.filter((r) => r.id !== id),
      opportunities: d.opportunities.filter((r) => r.id !== id),
    });
    try { setActions(await loadActions()); } catch (_) { /* ignore */ }
  };

  const dismiss = async (id) => {
    try {
      const res = await fetch(`${API}/${id}/dismiss`, { method: 'PATCH', credentials: 'include', headers: brandHeader() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData((d) => d && {
        ...d,
        risks: d.risks.filter((r) => r.id !== id),
        opportunities: d.opportunities.filter((r) => r.id !== id),
      });
    } catch (e) { toast.error('Kapatılamadı. ' + (e.message || '')); }
  };

  const rollback = async (actionId) => {
    if (!window.confirm('Bu aksiyonu geri al?')) return;
    try {
      const res = await fetch(`${API}/actions/${actionId}/rollback`, { method: 'POST', credentials: 'include', headers: { ...brandHeader(), 'Content-Type': 'application/json' }, body: '{}' });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      toast.success('Geri alındı');
      setActions(await loadActions());
    } catch (e) { toast.error('Geri alınamadı. ' + (e.message || '')); }
  };

  const approve = async (actionId, ok) => {
    try {
      const res = await fetch(`${API}/actions/${actionId}/approve`, { method: 'PATCH', credentials: 'include', headers: { ...brandHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ approve: ok }) });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      toast.success(ok ? 'Onaylandı' : 'Reddedildi');
      setActions(await loadActions());
    } catch (e) { toast.error('İşlem başarısız. ' + (e.message || '')); }
  };

  const counts = data?.counts || { total: 0, critical: 0, warning: 0, opportunity: 0 };
  // Reflect the most recent applied action's mode; bridge default is DRY-RUN.
  const lastReal = actions.find((a) => a.metrics_snapshot);
  const dryRun = lastReal ? !!lastReal.metrics_snapshot.dry_run : true;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-800">⚡ Şahlanma Komuta Merkezi</h1>
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${dryRun ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
            {dryRun ? 'DRY-RUN' : 'CANLI'}
          </span>
        </div>
        <button onClick={refresh} disabled={loading}
          className="text-sm px-3 py-1.5 rounded-lg bg-gray-800 text-white hover:bg-gray-700 disabled:opacity-50">
          {loading ? 'Taranıyor…' : '↻ Yenile'}
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-5">
        Algı (Insight) + Aksiyon (Smart Campaign Engine) tek ekranda. Önizle → gör, Uygula → onayla.
        Hiçbir şey otomatik canlıya gitmez.
        {data?.brandName && <span className="ml-1 font-medium text-gray-600">Marka: {data.brandName}.</span>}
        {data?.asOfDate && <span className="ml-1">Veri: {String(data.asOfDate).slice(0, 10)}</span>}
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        {[
          ['Toplam içgörü', counts.total, 'text-gray-800'],
          ['Kritik', counts.critical, 'text-rose-600'],
          ['Uyarı', counts.warning, 'text-amber-600'],
          ['Fırsat', counts.opportunity, 'text-emerald-600'],
          ['🔧 Hijyen', counts.hygiene || 0, 'text-teal-600'],
        ].map(([k, v, c]) => (
          <div key={k} className="rounded-xl border bg-white p-3">
            <p className="text-[11px] text-gray-500">{k}</p>
            <p className={`text-2xl font-bold ${c}`}>{v}</p>
          </div>
        ))}
      </div>

      {error && <p className="text-rose-600 text-sm mb-4">{error}</p>}

      {/* ALGI */}
      <h2 className="text-sm font-semibold text-gray-700 mb-2">🧠 ALGI — İçgörü Feed</h2>
      {loading && !data && <p className="text-gray-400 text-sm">Yükleniyor…</p>}
      {data && counts.total === 0 && !loading && (
        <div className="rounded-xl border border-dashed p-8 text-center text-gray-400 text-sm">
          🎉 Şu an aksiyon gerektiren içgörü yok.
        </div>
      )}
      {data?.risks?.length > 0 && (
        <div className="space-y-3 mb-4">
          {data.risks.map((i) => <Card key={i.id} ins={i} onApplied={onApplied} onDismiss={dismiss} />)}
        </div>
      )}
      {data?.opportunities?.length > 0 && (
        <div className="space-y-3 mb-4">
          {data.opportunities.map((i) => <Card key={i.id} ins={i} onApplied={onApplied} onDismiss={dismiss} />)}
        </div>
      )}

      {/* AKSIYONLAR & SONUCLAR */}
      <h2 className="text-sm font-semibold text-gray-700 mb-2 mt-8">🎬 AKSİYONLAR & SONUÇLAR</h2>
      <ActionsTable actions={actions} onRollback={rollback} onApprove={approve} />
    </div>
  );
}
